import React, { useRef, useState } from 'react';
import { PlayIcon } from 'lucide-react';
type YouTubeEmbedProps = {
  videoId: string;
  title: string;
  className?: string;
};

/**
 * Zeigt sofort das YouTube-Vorschaubild an und lädt den eigentlichen Player
 * erst beim Klick. Dadurch erscheint beim Scrollen direkt die Miniatur statt
 * eines schwarzen oder unscharfen Platzhalters.
 */
export function YouTubeEmbed({
  videoId,
  title,
  className = ''
}: YouTubeEmbedProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [thumbnail, setThumbnail] = useState(`https://i.ytimg.com/vi/${videoId}/maxresdefault.jpg`);
  const frameRef = useRef<HTMLIFrameElement>(null);

  // Manche Browser ignorieren "autoplay=1" beim ersten Laden. Sobald der Player
  // bereit ist, wird deshalb zusätzlich der Play-Befehl an YouTube geschickt,
  // damit ein einziger Klick genügt.
  const forcePlay = () => {
    const win = frameRef.current?.contentWindow;
    if (!win) return;
    const send = (message: Record<string, unknown>) => win.postMessage(JSON.stringify(message), '*');

    // Verbindung zur JS-API aufbauen und den Play-Befehl mehrfach senden,
    // bis der Player bereit ist – sonst wäre bei langsamen Browsern ein
    // zweiter Klick nötig.
    send({
      event: 'listening'
    });
    [0, 200, 450, 800, 1300, 2000].forEach(delay => window.setTimeout(() => send({
      event: 'command',
      func: 'playVideo',
      args: []
    }), delay));
  };
  if (isPlaying) {
    return <iframe ref={frameRef} onLoad={forcePlay} src={`https://www.youtube.com/embed/${videoId}?rel=0&autoplay=1&playsinline=1&enablejsapi=1`} title={title} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen className={`absolute inset-0 h-full w-full border-0 ${className}`} data-id="element-405" data-mp="1zebko53uvscj" />;
  }
  return <button type="button" onClick={() => setIsPlaying(true)} aria-label={`Video abspielen: ${title}`} className={`group absolute inset-0 h-full w-full overflow-hidden bg-neutral-900 ${className}`} data-id="element-406" data-mp="ix631zypo33l">
      <img src={thumbnail} alt={title} loading="eager" decoding="async" onError={() => setThumbnail(`https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`)} className="absolute inset-0 h-full w-full object-cover" data-id="element-407" data-mp="1hato4h9b9pag" />
      <span className="absolute inset-0 flex items-center justify-center" data-id="element-408" data-mp="256fmbhjp98tc">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-black/55 transition-colors duration-150 ease-out group-hover:bg-hot-500" data-id="element-409" data-mp="208nz30u02avh">
          <PlayIcon className="ml-1 h-7 w-7 fill-white text-white" aria-hidden="true" data-id="element-410" data-mp="2ewheolj35de2" />
        </span>
      </span>
    </button>;
}